import { useRef, useState, type FormEvent, type MouseEvent } from 'react';
import { AuthError, completePasswordReset, requestPasswordReset } from '@/server-threads';
import type { EntryCopy } from '@/i18n/ns/entry';
import { PasswordInput } from '@/components/ui/password-input';
import type { AuthStrings } from './strings';
import { entryErrorText } from './entry-copy';
import { EntryAlert, EntryField } from './entry-field';
import { EntryButton, EntryHeading } from './entry-shell';
import { EmailSentState } from './email-sent-state';
import {
  clearField,
  emailOnlyErrors,
  hasErrors,
  resetErrors,
  type EntryFieldName,
  type FieldErrors,
} from './entry-validation';

export function PasswordRecovery({
  mode,
  initialEmail,
  token,
  locale,
  entry,
  auth,
  onBack,
  onResetComplete,
}: {
  mode: 'forgot' | 'reset';
  initialEmail: string;
  token: string | null;
  /** The auth door's language, so the reset and changed emails match it. */
  locale: string;
  entry: EntryCopy;
  auth: AuthStrings;
  onBack: () => void;
  onResetComplete: () => void;
}) {
  const [email, setEmail] = useState(initialEmail);
  const [password, setPassword] = useState('');
  const [confirmation, setConfirmation] = useState('');
  const [phase, setPhase] = useState<'form' | 'sent' | 'complete'>('form');
  const [busy, setBusy] = useState(false);
  const [fields, setFields] = useState<FieldErrors>({});
  const [error, setError] = useState<string | null>(null);
  const emailRef = useRef<HTMLInputElement>(null);
  const text = (code: string) => entryErrorText(code, entry, auth);
  const change = (field: EntryFieldName) => {
    setFields((current) => clearField(current, field));
    setError(null);
  };

  const fail = (value: unknown, field: EntryFieldName) => {
    const code = value instanceof AuthError ? value.code : 'generic';
    if (code === 'invalid_input' && field === 'email') setFields({ email: 'email_invalid' });
    else if (
      code === 'password_too_short' ||
      code === 'password_too_long' ||
      code === 'password_compromised'
    )
      setFields({ password: code });
    else setError(text(code === 'invalid_input' ? 'generic' : code));
  };

  const request = async () => {
    const found = emailOnlyErrors({
      value: email,
      malformed: Boolean(emailRef.current?.validity?.typeMismatch),
    });
    setFields(found);
    if (hasErrors(found)) return;
    setBusy(true);
    setError(null);
    try {
      await requestPasswordReset(email.trim(), locale);
      setPhase('sent');
    } catch (value) {
      fail(value, 'email');
    } finally {
      setBusy(false);
    }
  };

  const complete = async () => {
    const found = resetErrors({ password, confirmation });
    setFields(found);
    if (hasErrors(found)) return;
    setBusy(true);
    setError(null);
    try {
      await completePasswordReset(token ?? '', password, locale);
      onResetComplete();
      setPhase('complete');
    } catch (value) {
      fail(value, 'password');
    } finally {
      setBusy(false);
    }
  };

  const submit = (event: FormEvent | MouseEvent) => {
    event.preventDefault();
    if (busy) return;
    void (mode === 'forgot' ? request() : complete());
  };

  if (phase === 'sent') {
    return (
      <EmailSentState
        copy={entry}
        lead={entry.resetSentTo}
        email={email.trim()}
        hint={entry.resetSentHint}
        back={{ label: entry.useAnotherEmail, onClick: () => setPhase('form') }}
      />
    );
  }

  if (phase === 'complete') {
    return (
      <>
        <EntryHeading title={entry.resetDoneTitle} lead={entry.resetDoneLead} />
        <div className="pc-entry-actions">
          <EntryButton block onClick={onBack}>
            {entry.continueToLogin}
          </EntryButton>
        </div>
      </>
    );
  }

  return (
    <>
      <EntryHeading
        title={mode === 'forgot' ? entry.forgotTitle : entry.resetTitle}
        lead={mode === 'forgot' ? entry.forgotLead : entry.resetLead}
      />
      <form className="pc-entry-form" noValidate onSubmit={submit}>
        {mode === 'forgot' ? (
          <EntryField label={entry.emailLabel} error={fields.email ? text(fields.email) : null}>
            {(control) => (
              <input
                {...control}
                ref={emailRef}
                type="email"
                autoComplete="email"
                value={email}
                onChange={(event) => {
                  setEmail(event.currentTarget.value);
                  change('email');
                }}
                className="pc-field-input"
              />
            )}
          </EntryField>
        ) : (
          <>
            <EntryField
              label={entry.newPasswordLabel}
              help={entry.passwordHelp}
              error={fields.password ? text(fields.password) : null}
            >
              {(control) => (
                <PasswordInput
                  {...control}
                  autoComplete="new-password"
                  value={password}
                  onChange={(event) => {
                    setPassword(event.currentTarget.value);
                    change('password');
                  }}
                  className="pc-field-input"
                  showLabel={auth.showPassword}
                  hideLabel={auth.hidePassword}
                />
              )}
            </EntryField>
            <EntryField
              label={entry.confirmPasswordLabel}
              error={fields.confirm ? text(fields.confirm) : null}
            >
              {(control) => (
                <PasswordInput
                  {...control}
                  autoComplete="new-password"
                  value={confirmation}
                  onChange={(event) => {
                    setConfirmation(event.currentTarget.value);
                    change('confirm');
                  }}
                  className="pc-field-input"
                  showLabel={auth.showPassword}
                  hideLabel={auth.hidePassword}
                />
              )}
            </EntryField>
          </>
        )}
        {error ? <EntryAlert>{error}</EntryAlert> : null}
        <EntryButton
          type="submit"
          block
          busy={busy}
          busyLabel={mode === 'forgot' ? entry.sending : entry.saving}
          onClick={submit}
        >
          {mode === 'forgot' ? entry.sendResetLink : entry.saveNewPassword}
        </EntryButton>
      </form>
    </>
  );
}
